import React from 'react';

import { useIntl } from 'react-intl';

import {
    gatewayPassNotAllowedReasonTranslationMap,
    identifierTypeTranslationMap,
} from 'src/features/eventLog/utils/constants';
import { eventMessages } from 'src/features/eventLog/utils/messages';
import {
    EVENT_TYPE,
    EventData,
    EventLog,
    GatewayClosed,
    GatewayOpened,
    SendGatewayCommand,
} from 'src/features/eventLog/types';
import { cn } from 'src/utils/shadcn';

type EventHandler = (eventData: EventData, event: EventLog) => string | null | React.ReactNode;

type CellEventLogMessageProps = {
    eventData: EventData;
    event: EventLog;
    onRefClick?: (ref: string) => void;
    handleIdentifierClick?: (identifier: string) => void;
    handleGatewayClick?: (gateway: string) => void;
};

const CellEventLogMessage = ({
    eventData,
    event,
    onRefClick,
    handleIdentifierClick,
    handleGatewayClick,
}: CellEventLogMessageProps) => {
    const { formatMessage, locale } = useIntl();

    // When the message references an identifier, render it as a clickable link that opens the identifier side panel or dashboard
    const renderLink = (linkName: string, onLinkClick?: (linkName: string) => void): React.ReactNode =>
        onLinkClick ? (
            <button
                type="button"
                onClick={() => onLinkClick(linkName)}
                className={cn('text-primary-700 hover:text-primary-600 cursor-pointer font-semibold')}
            >
                {linkName}
            </button>
        ) : (
            linkName
        );

    const eventHandlers: Record<string, EventHandler> = {
        license_plate: (data) => {
            const identifierEventType = event?.event_type;

            if ('license_plate' in data && identifierEventType === EVENT_TYPE.LPR_CAMERA_DETECTION) {
                return formatMessage(eventMessages.eventLogLPRCameraDetectionExitMessage, {
                    licensePlate: renderLink(data.license_plate, handleIdentifierClick),
                });
            }
            return null;
        },

        identifiers: (data, event) => {
            const identifierEventType = event?.event_type;

            if ('identifiers' in data && identifierEventType === EVENT_TYPE.VISITOR_IDENTIFIED) {
                const identifier = data.visitor.main_identifier.value;
                return formatMessage(eventMessages.eventLogVisitorIdentifierMessage, {
                    visitorIdentifierType: formatMessage(
                        identifierTypeTranslationMap[data.visitor?.main_identifier?.type],
                    ).toLowerCase(),
                    visitorIdentifier: renderLink(identifier, handleIdentifierClick),
                });
            }

            if ('identifiers' in data && identifierEventType === EVENT_TYPE.GATEWAY_PASS_NOT_ALLOWED) {
                const gatewayPassNotAllowedTitle = formatMessage(eventMessages.eventLogGatewayPassNotAllowedMessage, {
                    identifierType: formatMessage(identifierTypeTranslationMap[data.identifiers[0].type]).toLowerCase(),
                    licensePlate: renderLink(data.identifiers[0].value, handleIdentifierClick),
                });

                let reasonMessage;
                if ('reason' in data) {
                    if (gatewayPassNotAllowedReasonTranslationMap[data.reason]) {
                        reasonMessage = formatMessage(gatewayPassNotAllowedReasonTranslationMap[data.reason]);
                    } else {
                        reasonMessage = data.reason;
                    }
                }

                return (
                    <div className="flex flex-col">
                        <span>{gatewayPassNotAllowedTitle}</span>
                        {reasonMessage && <span className="font-bold">{reasonMessage}</span>}
                    </div>
                );
            }

            if ('identifiers' in data && identifierEventType === EVENT_TYPE.GATEWAY_PASS_ALLOWED) {
                return formatMessage(eventMessages.eventLogGatewayPassAllowedMessage, {
                    identifierType: formatMessage(identifierTypeTranslationMap[data.identifiers[0].type]).toLowerCase(),
                    licensePlate: renderLink(data.identifiers[0].value, handleIdentifierClick),
                });
            }

            if ('identifiers' in data && identifierEventType === EVENT_TYPE.ACTIVE_STAY_CREATED) {
                return formatMessage(eventMessages.eventLogActiveStayCreatedMessage, {
                    identifierType: formatMessage(identifierTypeTranslationMap[data.identifiers[0].type]).toLowerCase(),
                    identifierValue: renderLink(data.identifiers[0].value, handleIdentifierClick),
                });
            }
            return null;
        },

        identifier_type: (data, event) => {
            if (event.event_type === EVENT_TYPE.IDENTIFIER_CREATED && 'identifier_type' in data) {
                return formatMessage(eventMessages.eventLogIdentifierCreatedMessage, {
                    identifierType: formatMessage(identifierTypeTranslationMap[data?.identifier_type]).toLowerCase(),
                    licensePlate: renderLink(data?.value, handleIdentifierClick),
                });
            }
            return null;
        },
        access_key_value: (data, event) => {
            if (event.event_type === EVENT_TYPE.ACCESS_KEY_READER_DETECTION && 'access_key_value' in data) {
                return formatMessage(eventMessages.eventLogAccessKeyReaderDetectedMessage, {
                    activeStayRef: data.access_key_value,
                });
            }
            return null;
        },

        gateway: (data, event) => {
            if (event.event_type === EVENT_TYPE.GATEWAY_OPENED && 'gateway' in data) {
                const eventData = event.event_data as GatewayOpened;
                const gatewayName = eventData?.gateway?.names?.find((gate) => gate.language === locale)?.name;
                return formatMessage(eventMessages.eventLogGatewayOpenedMessage, {
                    gateway: gatewayName ? renderLink(gatewayName, handleGatewayClick) : '',
                });
            }
            if (event.event_type === EVENT_TYPE.GATEWAY_CLOSED && 'gateway' in data) {
                const eventData = event.event_data as GatewayClosed;
                const gatewayName = eventData?.gateway?.names?.find((gate) => gate.language === locale)?.name;

                return formatMessage(eventMessages.eventLogGatewayClosedMessage, {
                    gateway: gatewayName ? renderLink(gatewayName, handleGatewayClick) : '',
                });
            }
            if (
                (event.event_type === EVENT_TYPE.SEND_GATEWAY_OPEN_COMMAND ||
                    event.event_type === EVENT_TYPE.SEND_GATEWAY_CLOSE_COMMAND) &&
                'gateway' in data
            ) {
                const commandData = event.event_data as SendGatewayCommand;
                const gatewayName = commandData?.gateway?.names?.find((gate) => gate.language === locale)?.name;

                const title = formatMessage(
                    event.event_type === EVENT_TYPE.SEND_GATEWAY_OPEN_COMMAND
                        ? eventMessages.eventLogSendGatewayOpenCommandMessage
                        : eventMessages.eventLogSendGatewayCloseCommandMessage,
                    {
                        gateway: gatewayName ? renderLink(gatewayName, handleGatewayClick) : '',
                    },
                );

                // `reason` is an optional free-text field captured from the operator who
                // dispatched the command; surface it bold below the title, matching the
                // pattern used by the gateway_pass_not_allowed handler above.
                if (commandData.reason) {
                    return (
                        <div className="flex flex-col">
                            <span>{title}</span>
                            <span className="font-bold">{commandData.reason}</span>
                        </div>
                    );
                }
                return title;
            }
            return null;
        },
        active_stay_ref: (data, event) => {
            if (event.event_type === EVENT_TYPE.ACTIVE_STAY_PERIOD_COMPLETED && 'active_stay_ref' in data) {
                return formatMessage(eventMessages.eventLogStayPeriodCompletedMessage, {
                    activeStayRef: renderLink(data.active_stay_ref, onRefClick),
                });
            }
            if (event.event_type === EVENT_TYPE.ACTIVE_STAY_PERIOD_CREATED && 'active_stay_ref' in data) {
                return formatMessage(eventMessages.eventLogActiveStayPeriodCreatedMessage, {
                    activeStayRef: renderLink(data.active_stay_ref, onRefClick),
                });
            }
            return null;
        },
        past_stay_ref: (data, event) => {
            if (event.event_type === EVENT_TYPE.ACTIVE_STAY_COMPLETED && 'past_stay_ref' in data) {
                return formatMessage(eventMessages.eventLogActiveStayCompletedMessage, {
                    pastStayRef: renderLink(data.past_stay_ref, onRefClick),
                });
            }
            return null;
        },
    };

    const getEventLogMessage = (eventData: EventData, event: EventLog) => {
        for (const key in eventHandlers) {
            const message = eventHandlers[key](eventData, event);
            if (message) return message;
        }
        return '-';
    };

    // Wrap in a single element so the message renders as one flex item inside the (flex) table cell.
    // Otherwise text and the inline ref link become separate flex items - collapsing the spaces
    // between them and pushing the link off to the side
    return <div className="min-w-0">{getEventLogMessage(eventData, event)}</div>;
};

export default CellEventLogMessage;
